import { blue } from '@ant-design/colors'
import {
  AimOutlined,
  EnvironmentOutlined,
  ProfileOutlined,
  TableOutlined,
} from '@ant-design/icons'
import Cluster from '@components/cluster/Cluster'
import { HelpButton } from '@components/help/HelpButton'
import Page from '@components/Page/Page'
import { Venue } from '@components/venue/Venue'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { Cluster as ClusterModel } from '@models/Cluster'
import {
  getClusters,
  selectClusters,
  setClusterQuery,
} from '@store/ClusterReducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import {
  Button,
  Card,
  Col,
  Segmented,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { useEffect } from 'react'

const { Text } = Typography

type ViewMode = 'list' | 'map'

const ClustersPage = () => {
  const dispatch = useAppDispatch()
  const clusters = useAppSelector(selectClusters())
  const { query, isLoading } = useAppSelector((state) => state.cluster)
  const { isOpen } = useSBRM()

  const columns: TableColumnsType<ClusterModel> = [
    {
      title: 'Name',
      width: 150,
      render: (record: ClusterModel) => (
        <Space>
          <Text>{record.name}</Text>
        </Space>
      ),
    },
    {
      title: 'Titles',
      render: (record: ClusterModel) => <Cluster.Avatar model={record} />,
    },
    {
      title: 'Venues',
      render: (record: ClusterModel) => (
        <Venue.AvatarGroup ids={record.venues} />
      ),
    },
    {
      title: 'Coordinates',
      render: (record: ClusterModel) => (
        <Space>
          <Tag color="blue">
            <AimOutlined />
            <Text
              style={{ maxWidth: 60, color: blue[6] }}
              ellipsis={{ tooltip: record.latitude }}
            >
              {record.latitude}
            </Text>
            <Text
              style={{ maxWidth: 60, color: blue[6] }}
              ellipsis={{ tooltip: record.longitude }}
            >
              , {record.longitude}
            </Text>
          </Tag>
        </Space>
      ),
    },
    {
      align: 'right',
      render: (record: ClusterModel) => (
        <Space>
          <SBRM.Actions
            actions={[SBRMAction.View]}
            entity={Entity.cluster}
            entityId={record.id}
            trigger={
              <Tooltip title="Activity log">
                <Button icon={<ProfileOutlined />} />
              </Tooltip>
            }
          />
          <SBRM.Actions
            actions={[SBRMAction.Update, SBRMAction.Delete]}
            entity={Entity.cluster}
            entityId={record.id}
          />
        </Space>
      ),
    },
  ]

  const fetchClusters = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
    }
    dispatch(setClusterQuery(newQuery))
    dispatch(getClusters(newQuery))
  }

  const { searchParams, setSearchParams, handleTableChange, params } =
    useTableUrlSync<ClusterModel>({ onParamsChange: fetchClusters })

  const viewMode = (searchParams.get('viewMode') as ViewMode) || 'list'

  useEffect(() => {
    if (isOpen) return

    fetchClusters(params)
  }, [isOpen])

  return (
    <Page permissions={Permission.listCluster} noWrapWithCol>
      <Col span={12}>
        <PageTitle level={4} title="What are clusters?" />
        <Card>
          <Space orientation="vertical">
            <Text>
              Clusters are groups of venues that are geographically close to one
              another. They help structure and organize venue listings, making
              it easier for users to browse and discover relevant places.
              Instead of displaying all venues individually, clusters allow for
              a more intuitive navigation experience within the app.
            </Text>
            <HelpButton articleId="50" block label="Read the documentation" />
          </Space>
        </Card>
      </Col>
      <Col span={12}>
        <PageTitle level={4} title="Closest cluster title" />
        <Cluster.ClosestClusterTitle />
      </Col>

      <Col span={24}>
        <PageTitle
          level={4}
          title="Clusters"
          toolbar={
            <Space>
              <Segmented
                value={viewMode}
                onChange={(value) => {
                  setSearchParams(
                    (prev) => {
                      if (value === 'list') {
                        prev.delete('viewMode')
                      } else {
                        prev.set('viewMode', 'map')
                      }
                      return prev
                    },
                    { replace: true }
                  )
                }}
                options={[
                  {
                    label: 'List',
                    value: 'list',
                    icon: <TableOutlined />,
                  },
                  {
                    label: 'Map',
                    value: 'map',
                    icon: <EnvironmentOutlined />,
                  },
                ]}
              />
              <SBRM.Actions
                actions={[SBRMAction.Create]}
                entity={Entity.cluster}
              />
            </Space>
          }
        />

        {viewMode === 'list' ? (
          <Table
            columns={columns}
            dataSource={clusters}
            loading={isLoading}
            onChange={handleTableChange}
            pagination={query.pagination}
          />
        ) : (
          <Cluster.Map models={clusters} height={500} />
        )}
      </Col>
    </Page>
  )
}

export default ClustersPage
